import type { IconName } from "@mdotm/shadcn/legacy/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { renderNodeOrFn, type NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { type ReactNode, useState } from "react";
import { SideDrawer } from "../common/SideDrawer";

export const smallTabWidth = 24;
export const extendedTabWidth = 364;

type Filter<T> = {
	component: NodeOrFn<{ value: T; onChange(newValue: T): void; children?: ReactNode }>;
	children?: Array<Filter<T>>;
};

type SidePanelWithFiltersProps<T> = {
	toggleIcon: IconName;
	title: NodeOrFn<{
		expand: boolean;
		onExpandChange(expand: boolean): void;
	}>;
	value: T;
	onChange(newValue: T): MaybePromise<void>;
	filters: Array<Filter<T>>;
};

function SidePanelWithFilters<T>({
	title,
	toggleIcon,
	filters,
	onChange,
	value,
}: SidePanelWithFiltersProps<T>): React.JSX.Element {
	const [expand, setExpand] = useState(true);

	return (
		<SideDrawer
			title={title}
			toggleIcon={toggleIcon}
			toggleOffset={64}
			expand={expand}
			onExpandChange={setExpand}
			widths={[smallTabWidth, extendedTabWidth]}
		>
			<div className="relative z-0">
				<SidePanelWithFiltersRender value={value} onChange={onChange} filters={filters} />
			</div>
		</SideDrawer>
	);
}

export default SidePanelWithFilters;

function SidePanelWithFiltersRender<T>({
	filters,
	...params
}: {
	filters: Array<Filter<T>>;
	value: T;
	onChange(newValue: T): MaybePromise<void>;
}) {
	return (
		<ForEach collection={filters}>
			{(item) => {
				if (!item.children) {
					return <>{renderNodeOrFn(item.component, { ...params })}</>;
				}

				return renderNodeOrFn(item.component, {
					...params,
					children: <SidePanelWithFiltersRender {...params} filters={item.children} />,
				});
			}}
		</ForEach>
	);
}
